// MIDI input → instrument tracks. Two sources:
//  - "Musical typing": the computer keyboard (when enabled) plays notes.
//  - Web MIDI: any connected hardware MIDI keyboard (always live).
// Notes route to the record-armed instrument track(s); if none armed, to the
// selected instrument track; else the most recent instrument track.
import { Store } from "../state/store";
import { AudioEngine } from "./engine";
import { MidiNote } from "../state/model";

// QWERTY → semitone offset (one octave + a bit), Ableton-style layout.
const KEY_SEMITONE: Record<string, number> = {
  a: 0, w: 1, s: 2, e: 3, d: 4, f: 5, t: 6, g: 7, y: 8, h: 9, u: 10, j: 11, k: 12, o: 13, l: 14, p: 15,
};

export class MidiInput {
  typing = false;          // musical-typing enabled
  octave = 0;              // octave shift (z/x)
  onState?: () => void;    // notify UI (toolbar label/active state)
  private down = new Set<number>();   // currently-held midi notes (computer kbd)
  // active MIDI recording: per armed instrument track, finished notes + held onsets
  private rec: { startPos: number; tracks: Map<string, { notes: MidiNote[]; active: Map<number, { start: number; vel: number }> }> } | null = null;

  private webMidiInit = false;

  constructor(private store: Store, private engine: AudioEngine) {
    // Computer-keyboard listeners need no permission; Web MIDI access is requested
    // lazily (see ensureWebMidi) so we don't prompt users who never touch MIDI.
    window.addEventListener("keydown", (e) => this.onKeyDown(e), true);
    window.addEventListener("keyup", (e) => this.onKeyUp(e), true);
  }

  /** Request hardware MIDI access — call only once a MIDI feature is used
   *  (e.g. an instrument track is added). Idempotent; prompts at most once. */
  ensureWebMidi() {
    if (this.webMidiInit) return;
    this.webMidiInit = true;
    this.initWebMidi();
  }

  setTyping(on: boolean) {
    this.typing = on;
    if (!on) this.allOff();
    else this.engine.resume();
    this.onState?.();
  }

  private baseMidi() { return 60 + this.octave * 12; }

  private targets(): string[] {
    const inst = this.store.project.tracks.filter((t) => t.kind === "instrument");
    if (!inst.length) return [];
    const armed = inst.filter((t) => t.armed);
    if (armed.length) return armed.map((t) => t.id);
    const sel = inst.find((t) => t.id === this.store.selectedTrackId);
    return [sel ? sel.id : inst[inst.length - 1].id];
  }

  noteOn(midi: number, vel = 0.9) {
    for (const id of this.targets()) this.engine.instrument(id)?.noteOn(midi, vel);
    if (this.rec) { const now = this.engine.position(); for (const st of this.rec.tracks.values()) st.active.set(midi, { start: now, vel }); }
  }
  noteOff(midi: number) {
    for (const id of this.targets()) this.engine.instrument(id)?.noteOff(midi);
    if (this.rec) {
      const now = this.engine.position();
      for (const st of this.rec.tracks.values()) {
        const a = st.active.get(midi);
        if (a) { st.notes.push({ midi, start: Math.max(0, a.start - this.rec.startPos), dur: Math.max(0.02, now - a.start), vel: a.vel }); st.active.delete(midi); }
      }
    }
  }

  /** Begin capturing notes into the record-armed instrument tracks. */
  startRecording(pos: number) {
    const tracks = new Map<string, { notes: MidiNote[]; active: Map<number, { start: number; vel: number }> }>();
    for (const t of this.store.project.tracks) if (t.kind === "instrument" && t.armed) tracks.set(t.id, { notes: [], active: new Map() });
    this.rec = tracks.size ? { startPos: pos, tracks } : null;
  }
  /** Finish capture → recorded notes per track (clip-relative). */
  stopRecording(): Map<string, MidiNote[]> {
    const out = new Map<string, MidiNote[]>();
    if (!this.rec) return out;
    const end = this.engine.position();
    for (const [id, st] of this.rec.tracks) {
      for (const [midi, a] of st.active) st.notes.push({ midi, start: Math.max(0, a.start - this.rec.startPos), dur: Math.max(0.02, end - a.start), vel: a.vel });
      out.set(id, st.notes);
    }
    this.rec = null;
    return out;
  }
  isRecording() { return !!this.rec; }

  private isTextTarget(t: EventTarget | null) {
    const el = t as HTMLElement | null;
    if (!el) return false;
    const tag = el.tagName;
    return tag === "INPUT" || tag === "SELECT" || tag === "TEXTAREA" || el.isContentEditable;
  }

  private onKeyDown(e: KeyboardEvent) {
    if (!this.typing || e.repeat || e.ctrlKey || e.metaKey || e.altKey) return;
    if (this.isTextTarget(e.target)) return;
    const k = e.key.toLowerCase();
    if (k === "z") { this.octave = Math.max(-3, this.octave - 1); this.onState?.(); e.preventDefault(); e.stopPropagation(); return; }
    if (k === "x") { this.octave = Math.min(3, this.octave + 1); this.onState?.(); e.preventDefault(); e.stopPropagation(); return; }
    const semi = KEY_SEMITONE[k];
    if (semi === undefined) return;
    const midi = this.baseMidi() + semi;
    e.preventDefault(); e.stopPropagation(); // claim the key so app shortcuts don't fire
    if (this.down.has(midi)) return;
    this.down.add(midi);
    this.engine.resume();
    this.noteOn(midi);
  }

  private onKeyUp(e: KeyboardEvent) {
    if (!this.typing) return;
    const semi = KEY_SEMITONE[e.key.toLowerCase()];
    if (semi === undefined) return;
    const midi = this.baseMidi() + semi;
    if (this.down.delete(midi)) { e.preventDefault(); e.stopPropagation(); this.noteOff(midi); }
  }

  private allOff() {
    for (const m of [...this.down]) this.noteOff(m);
    this.down.clear();
  }

  private async initWebMidi() {
    const nav = navigator as any;
    if (!nav.requestMIDIAccess) return;
    try {
      const access = await nav.requestMIDIAccess();
      const attach = (input: any) => { input.onmidimessage = (m: any) => this.onMidiMessage(m.data); };
      access.inputs.forEach(attach);
      access.onstatechange = (e: any) => { if (e.port && e.port.type === "input" && e.port.state === "connected") attach(e.port); };
    } catch { /* no MIDI access */ }
  }

  private onMidiMessage(data: Uint8Array) {
    const status = data[0] & 0xf0;
    const note = data[1], vel = data[2];
    if (status === 0x90 && vel > 0) { this.engine.resume(); this.noteOn(note, vel / 127); }
    else if (status === 0x80 || (status === 0x90 && vel === 0)) this.noteOff(note);
  }
}
